/**
 * La cola de altas: lo que llego por el formulario publico, esperando a que
 * alguien decida.
 *
 * ---------------------------------------------------------------------------
 * ESTA PANTALLA ES LA FRONTERA
 *
 * Al otro lado del formulario no hay nadie conocido: lo que se pinta aqui lo
 * escribio un desconocido sobre si mismo, sin verificar. Por eso todo lo que
 * viene de la solicitud se pinta con `literal()` --tal como llego, en cualquier
 * idioma o alfabeto y sin limpiar [CLAUDE.md, Idioma, categoria 2]-- y por eso
 * el boton de aprobar dice exactamente lo que va a escribir en el directorio
 * antes de escribirlo.
 *
 * Aprobar no es "aceptar un registro": es crear un `tercero` que mañana estara
 * en la lista de destinatarios de un correo que sale de la empresa.
 *
 * ---------------------------------------------------------------------------
 * LAS SEÑALES DE DUPLICADO NO BLOQUEAN
 *
 * El correo que ya esta en la agenda, el nombre homonimo y el numero de WCA que
 * ya tiene otro [H75] se enseñan arriba, antes que nada, con la accion que
 * corresponde --enganchar en vez de crear-- pero ninguna impide aprobar. Es la
 * misma linea que el resto del modulo: proponer no es decidir.
 *
 * ---------------------------------------------------------------------------
 * LO QUE SE VA A CREAR SE VE Y SE PUEDE QUITAR
 *
 * Las especialidades son el producto de los modos por los trades declarados, y
 * salen marcadas una por una. Un proveedor que marco tres modos y dos trades a
 * cada lado propone doce filas de directorio; aprobar a ciegas las mete todas.
 * Aqui se ven y se desmarcan, que es lo unico que convierte "aprobado" en una
 * decision en vez de un clic.
 */

import type { CreadoAlta, SolicitudAlta } from '../api';
import { dato, el, literal } from '../dom';
import { formatearFecha, type Idioma } from '../formato';
import type { Traductor } from '../i18n';

/** Una especialidad candidata, con si esta marcada. */
export interface EspecialidadPropuesta {
  readonly modo: string;
  readonly origen: string;
  readonly destino: string;
  readonly marcada: boolean;
}

/** El formulario de decision. Vive en `app.ts`; aqui solo se pinta. */
export interface EstadoDecision {
  readonly nombre: string;
  readonly pais: string;
  readonly idioma: string;
  /** Enganchar al tercero que ya tiene ese correo en vez de crear uno. */
  readonly engancharA: string | null;
  readonly especialidades: readonly EspecialidadPropuesta[];
  readonly motivo: string;
}

/**
 * El producto de modos por trades, todo marcado.
 *
 * Una solicitud que solo trajo texto libre no propone ninguna: no hay region
 * que poner, y la ruta se queda en `alcance_literal`. La pantalla lo dice en
 * vez de enseñar una lista vacia sin explicacion.
 */
export function especialidadesPropuestas(s: SolicitudAlta): EspecialidadPropuesta[] {
  return s.modos.flatMap((modo) =>
    s.origenes.flatMap((origen) =>
      s.destinos.map((destino) => ({ modo, origen, destino, marcada: true })),
    ),
  );
}

export function decisionInicial(s: SolicitudAlta): EstadoDecision {
  return {
    nombre: s.empresa,
    pais: '',
    idioma: s.idioma,
    // Se propone enganchar cuando el correo ya esta en la agenda, pero no se
    // decide: la casilla sale marcada y se puede quitar.
    engancharA: s.contacto_conocido_tercero_id,
    especialidades: especialidadesPropuestas(s),
    motivo: '',
  };
}

// ---------------------------------------------------------------------------

function chip(texto: string, clase = 'chip'): HTMLElement {
  return el('span', { clase, texto });
}

function chips(valores: readonly string[], clase = 'chip'): HTMLElement {
  return el(
    'div',
    { clase: 'chips' },
    valores.map((v) => chip(v, clase)),
  );
}

export interface OpcionesLista {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly solicitudes: readonly SolicitudAlta[];
  readonly todas: boolean;
  readonly cargando: boolean;
  readonly onVerTodas: (todas: boolean) => void;
  readonly onAbrir: (id: string) => void;
  /**
   * Salir de la lista.
   *
   * Opcional desde que hay menu lateral: esta lista es la raiz de su zona, asi
   * que quien quiera irse usa el menu. Un "Volver" que salta a otra zona
   * distinta es justo el control sin destino claro que despista a quien acaba
   * de llegar. Se conserva para cuando la lista se abra desde otro sitio.
   */
  readonly onVolver?: () => void;
}

export function pintarListaAltas(o: OpcionesLista): HTMLElement {
  const { t } = o;

  const filas = o.solicitudes.map((s) =>
    el('tr', {}, [
      el('td', {}, [
        el('button', {
          clase: 'boton boton-plano boton-menor',
          texto: s.folio,
          attrs: { type: 'button' },
          onClick: () => {
            o.onAbrir(s.id);
          },
        }),
      ]),
      el('td', {}, [
        literal(s.empresa),
        el('div', { clase: 'tenue' }, [literal(s.pais_literal)]),
      ]),
      el('td', {}, [literal(s.contacto_email)]),
      el('td', {}, [chips(s.modos, 'chip')]),
      el('td', { texto: formatearFecha(s.recibida_en, o.idioma) }),
      el('td', {}, [
        chip(t(`alta.estado.${s.estado}`), `chip-estado chip-${s.estado.toLowerCase()}`),
        // La señal fuerte, tambien en la lista: es la que cambia la decision
        // antes de abrirla.
        s.contacto_conocido_tercero_id === null
          ? null
          : chip(t('alta.senal.conocidoCorto'), 'chip-atencion'),
      ]),
    ]),
  );

  return el('section', { clase: 'panel panel-rfq-lista' }, [
    el('div', { clase: 'rfq-cabecera' }, [
      el('h2', { texto: t('alta.lista.titulo') }),
      el('div', { clase: 'rfq-acciones' }, [
        el('label', { clase: 'casilla' }, [
          (() => {
            const c = el('input', { attrs: { type: 'checkbox' } });
            c.checked = o.todas;
            c.addEventListener('change', () => {
              o.onVerTodas(c.checked);
            });
            return c;
          })(),
          el('span', { texto: t('alta.lista.verTodas') }),
        ]),
        o.onVolver === undefined
          ? null
          : el('button', {
              clase: 'boton boton-plano',
              texto: t('rfq.volver'),
              attrs: { type: 'button' },
              onClick: o.onVolver,
            }),
      ]),
    ]),

    o.cargando ? el('p', { clase: 'tenue', texto: t('alta.lista.cargando') }) : null,

    o.solicitudes.length === 0 && !o.cargando
      ? el('p', { clase: 'tenue', texto: t('alta.lista.vacia') })
      : el('table', { clase: 'tabla-solicitudes' }, [
          el('thead', {}, [
            el('tr', {}, [
              el('th', { texto: t('alta.col.folio') }),
              el('th', { texto: t('alta.col.empresa') }),
              el('th', { texto: t('alta.col.correo') }),
              el('th', { texto: t('alta.col.modos') }),
              el('th', { texto: t('alta.col.recibida') }),
              el('th', { texto: t('alta.col.estado') }),
            ]),
          ]),
          el('tbody', {}, filas),
        ]),
  ]);
}

// ---------------------------------------------------------------------------

export interface OpcionesDetalle {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly solicitud: SolicitudAlta;
  readonly decision: EstadoDecision;
  readonly trabajando: boolean;
  readonly creado: CreadoAlta | null;
  readonly onCambio: (d: EstadoDecision) => void;
  readonly onAprobar: () => void;
  readonly onRechazar: () => void;
  readonly onVolver: () => void;
}

function senales(o: OpcionesDetalle): HTMLElement | null {
  const { t, solicitud: s } = o;
  const avisos: HTMLElement[] = [];

  if (s.contacto_conocido_tercero_id !== null) {
    avisos.push(
      el('p', {}, [
        el('strong', { texto: t('alta.senal.conocido') }),
        el('span', { texto: ' ' }),
        literal(s.contacto_conocido_nombre ?? s.contacto_email),
      ]),
    );
  }
  if (s.homonimos !== null && s.homonimos.length > 0) {
    avisos.push(
      el('p', {}, [
        el('strong', { texto: t('alta.senal.homonimo') }),
        el('span', { texto: ' ' }),
        literal(s.homonimos.join(', ')),
      ]),
    );
  }
  if (s.licencias_tomadas !== null && s.licencias_tomadas.length > 0) {
    // [H75] O es el mismo proveedor entrando dos veces, o dos empresas
    // reclamando el mismo numero. Las dos cosas las resuelve una persona.
    avisos.push(
      el('p', {}, [
        el('strong', { texto: t('alta.senal.licencia') }),
        el('span', { texto: ` ${s.licencias_tomadas.join(', ')}` }),
      ]),
    );
  }
  if (s.otras_pendientes > 0) {
    avisos.push(
      el('p', {
        texto: t('alta.senal.otras', { n: String(s.otras_pendientes) }),
      }),
    );
  }

  if (avisos.length === 0) return null;
  return el('div', { clase: 'aviso' }, avisos);
}

function loQueDijo(o: OpcionesDetalle): HTMLElement {
  const { t, solicitud: s } = o;
  return el('div', { clase: 'rfq-bloque' }, [
    el('h3', { texto: t('alta.bloque.dijo') }),
    el('div', { clase: 'rfq-datos' }, [
      dato(t('alta.campo.empresa'), literal(s.empresa)),
      dato(t('alta.campo.pais'), literal(s.pais_literal)),
      dato(t('alta.campo.sitio'), literal(s.sitio_web)),
      dato(t('alta.campo.contacto'), literal(s.contacto_nombre)),
      dato(t('alta.campo.correo'), literal(s.contacto_email)),
      dato(t('alta.campo.telefono'), literal(s.contacto_telefono)),
      dato(t('alta.campo.idioma'), s.idioma),
      dato(t('alta.campo.recibida'), formatearFecha(s.recibida_en, o.idioma)),
    ]),
    el('div', { clase: 'rfq-datos' }, [
      dato(t('alta.campo.modos'), chips(s.modos)),
      dato(
        t('alta.campo.trades'),
        s.origenes.length === 0
          ? null
          : el('span', {}, [
              chips(s.origenes),
              el('span', { clase: 'tenue', texto: ' → ' }),
              chips(s.destinos),
            ]),
      ),
    ]),
    // El alcance como lo escribio: es lo que acaba en `alcance_literal` y lo
    // unico que queda si la normalizacion en regiones se queda corta.
    s.rutas_literal === null
      ? null
      : el('p', { clase: 'alcance-literal' }, [literal(s.rutas_literal)]),
    s.membresias.length === 0
      ? null
      : el('div', { clase: 'rfq-datos' }, [
          dato(
            t('alta.campo.membresias'),
            chips(
              s.membresias.map((m) =>
                m.numero === null ? m.tipo : `${m.tipo} ${m.numero}`,
              ),
            ),
          ),
        ]),
    s.comentario === null ? null : el('p', {}, [literal(s.comentario)]),
  ]);
}

function resultado(o: OpcionesDetalle): HTMLElement | null {
  const { t, creado: c } = o;
  if (c === null) return null;

  const notas: HTMLElement[] = [
    el('p', {}, [
      el('strong', {
        texto: t(c.tercero === 'CREADO' ? 'alta.hecho.creado' : 'alta.hecho.enganchado'),
      }),
      el('span', { texto: ' ' }),
      literal(c.tercero_nombre),
    ]),
    el('p', {
      texto: t('alta.hecho.especialidades', {
        n: String(c.especialidades),
        repetidas: String(c.especialidades_repetidas),
      }),
    }),
  ];

  if (c.licencias > 0) {
    notas.push(el('p', { texto: t('alta.hecho.licencias', { n: String(c.licencias) }) }));
  }
  // Lo que NO se creo, y por que. Un resumen que solo cuenta lo que salio bien
  // deja al operador creyendo que la licencia quedo registrada.
  for (const om of c.licencias_omitidas) {
    notas.push(
      el('p', {
        clase: 'tenue',
        texto: `${om.tipo}${om.numero === null ? '' : ` ${om.numero}`}: ${t(
          `alta.hecho.omitida.${om.motivo}`,
        )}`,
      }),
    );
  }
  if (c.trades_no_reconocidos.length > 0) {
    notas.push(
      el('p', {
        clase: 'tenue',
        texto: t('alta.hecho.tradesRaros', { t: c.trades_no_reconocidos.join(', ') }),
      }),
    );
  }

  return el('div', { clase: 'aviso aviso-neutro' }, notas);
}

function formularioDecision(o: OpcionesDetalle): HTMLElement {
  const { t, decision: d } = o;

  const texto = (
    id: string,
    etiqueta: string,
    valor: string,
    cambia: (v: string) => void,
    maximo?: number,
  ): HTMLElement => {
    const control = el('input', {
      clase: 'control',
      attrs: {
        id,
        type: 'text',
        ...(maximo === undefined ? {} : { maxlength: String(maximo) }),
      },
    });
    control.value = valor;
    control.addEventListener('input', () => {
      cambia(control.value);
    });
    return el('div', { clase: 'campo' }, [
      el('label', { clase: 'etiqueta', texto: etiqueta, attrs: { for: id } }),
      control,
    ]);
  };

  const engancharDisponible = o.solicitud.contacto_conocido_tercero_id;

  return el('div', { clase: 'rfq-bloque' }, [
    el('h3', { texto: t('alta.bloque.decidir') }),

    engancharDisponible === null
      ? null
      : el('label', { clase: 'casilla campo-casilla' }, [
          (() => {
            const c = el('input', { attrs: { type: 'checkbox' } });
            c.checked = d.engancharA !== null;
            c.addEventListener('change', () => {
              o.onCambio({ ...d, engancharA: c.checked ? engancharDisponible : null });
            });
            return c;
          })(),
          el('span', {}, [
            el('span', { texto: `${t('alta.decidir.enganchar')} ` }),
            literal(o.solicitud.contacto_conocido_nombre ?? ''),
          ]),
        ]),

    // Con el tercero enganchado, el nombre y el pais son del que ya existe: no
    // se piden porque no se van a usar, y un campo que no hace nada es peor que
    // uno que falta.
    d.engancharA !== null
      ? null
      : el('div', { clase: 'rfq-datos' }, [
          texto('alta-nombre', t('alta.decidir.nombre'), d.nombre, (v) => {
            o.onCambio({ ...d, nombre: v });
          }),
          texto(
            'alta-pais',
            t('alta.decidir.pais'),
            d.pais,
            (v) => {
              o.onCambio({ ...d, pais: v.toUpperCase() });
            },
            2,
          ),
        ]),
    d.engancharA !== null
      ? null
      : el('p', { clase: 'ayuda tenue', texto: t('alta.decidir.paisAyuda') }),

    el('h4', { texto: t('alta.decidir.especialidades') }),
    d.especialidades.length === 0
      ? // Ni una: solo trajo texto libre. Se dice, porque una lista vacia sin
        // explicacion se lee como un fallo de la pantalla.
        el('p', { clase: 'tenue', texto: t('alta.decidir.sinEspecialidades') })
      : el(
          'div',
          { clase: 'lista-especialidades' },
          d.especialidades.map((e, i) =>
            el('label', { clase: 'casilla campo-casilla' }, [
              (() => {
                const c = el('input', { attrs: { type: 'checkbox' } });
                c.checked = e.marcada;
                c.addEventListener('change', () => {
                  const copia = [...d.especialidades];
                  copia[i] = { ...e, marcada: c.checked };
                  o.onCambio({ ...d, especialidades: copia });
                });
                return c;
              })(),
              el('span', { texto: `${e.modo} · ${e.origen} → ${e.destino}` }),
            ]),
          ),
        ),

    el('div', { clase: 'campo' }, [
      el('label', {
        clase: 'etiqueta',
        texto: t('alta.decidir.motivo'),
        attrs: { for: 'alta-motivo' },
      }),
      (() => {
        const area = el('textarea', {
          clase: 'control',
          attrs: { id: 'alta-motivo', rows: '2' },
        });
        area.value = d.motivo;
        area.addEventListener('input', () => {
          o.onCambio({ ...d, motivo: area.value });
        });
        return area;
      })(),
      el('p', { clase: 'ayuda tenue', texto: t('alta.decidir.motivoAyuda') }),
    ]),

    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-primario',
        texto: t('alta.decidir.aprobar'),
        attrs: { type: 'button', ...(o.trabajando ? { disabled: 'disabled' } : {}) },
        onClick: o.onAprobar,
      }),
      el('button', {
        clase: 'boton',
        texto: t('alta.decidir.rechazar'),
        attrs: {
          type: 'button',
          // Sin motivo no se puede rechazar: la base tampoco lo admite, y
          // desactivar el boton lo dice antes de gastar un viaje.
          ...(o.trabajando || d.motivo.trim() === '' ? { disabled: 'disabled' } : {}),
        },
        onClick: o.onRechazar,
      }),
      el('button', {
        clase: 'boton boton-plano',
        texto: t('alta.decidir.volver'),
        attrs: { type: 'button' },
        onClick: o.onVolver,
      }),
    ]),
  ]);
}

export function pintarAlta(o: OpcionesDetalle): HTMLElement {
  const { t, solicitud: s } = o;
  const decidida = s.estado !== 'PENDIENTE';

  return el('section', { clase: 'panel panel-rfq-solicitud' }, [
    el('div', { clase: 'rfq-cabecera' }, [
      el('h2', {}, [el('span', { texto: `${s.folio} · ` }), literal(s.empresa)]),
      el('div', { clase: 'rfq-acciones' }, [
        chip(t(`alta.estado.${s.estado}`), `chip-estado chip-${s.estado.toLowerCase()}`),
        el('button', {
          clase: 'boton boton-plano',
          texto: t('alta.decidir.volver'),
          attrs: { type: 'button' },
          onClick: o.onVolver,
        }),
      ]),
    ]),

    decidida ? null : senales(o),
    resultado(o),
    loQueDijo(o),

    decidida
      ? el('div', { clase: 'rfq-bloque' }, [
          el('div', { clase: 'rfq-datos' }, [
            dato(t('alta.campo.revisada'), formatearFecha(s.revisada_en, o.idioma)),
            dato(t('alta.campo.revisor'), s.revisada_por_nombre),
            dato(t('alta.campo.tercero'), literal(s.tercero_nombre)),
            dato(t('alta.campo.motivo'), literal(s.motivo_rechazo)),
          ]),
        ])
      : formularioDecision(o),
  ]);
}
